import max from 'lodash/max'
import { format } from 'date-fns'
import {
  calcImplSumAmount,
  calcSumAmount,
  correctedPriceNoVat,
  priceNoVat,
  priceSumNoVat,
  sumNoVatByUnitWithCorrect,
  sumVatByUnitWithCorrect,
} from '../../../utils/calculation'
import {
  buildCell,
  buildMainHeaderCell,
  buildMergedHeaderCell,
  buildProjectName,
  buildReportDate,
  buildSubHeaderCell,
} from '../report-utils'
import { getOffBudget } from '../../application/app-view-utils'

const ExcelJS = require('exceljs')
const FileSaver = require('file-saver')

const buildHeader = (worksheet, start = 1) => {
  const defaultWidth = 20
  buildMergedHeaderCell(worksheet, 'A', 'Наименование заявка', start)
  buildMergedHeaderCell(worksheet, 'B', 'Наименование Факт', start)
  buildMergedHeaderCell(worksheet, 'C', 'Категория', start)
  buildMergedHeaderCell(worksheet, 'D', 'Вне Сметы', start)
  buildMergedHeaderCell(worksheet, 'E', 'Наименование Смета', start)

  buildMainHeaderCell(worksheet, 6, 9, 'Смета', start)
  buildSubHeaderCell(worksheet, 6, 'Кол-во', defaultWidth, start)
  buildSubHeaderCell(worksheet, 7, 'Ед.изм.', defaultWidth, start)
  buildSubHeaderCell(worksheet, 8, 'Цена за ед, без НДС', 25, start)
  buildSubHeaderCell(worksheet, 9, 'Сумма, без НДС', defaultWidth, start)

  buildMainHeaderCell(worksheet, 10, 13, 'Заявка', start)
  buildSubHeaderCell(worksheet, 10, 'Заявка', defaultWidth, start)
  buildSubHeaderCell(worksheet, 11, 'Дата', defaultWidth, start)
  buildSubHeaderCell(worksheet, 12, 'Кол-во', defaultWidth, start)
  buildSubHeaderCell(worksheet, 13, 'Ед.изм.', defaultWidth, start)

  buildMainHeaderCell(worksheet, 14, 22, 'Факт', start)
  buildSubHeaderCell(worksheet, 14, 'Кол-во', defaultWidth, start)
  buildSubHeaderCell(worksheet, 15, 'Ед.изм.', defaultWidth, start)
  buildSubHeaderCell(worksheet, 16, 'Корректировка кол-ва', 25, start)
  buildSubHeaderCell(worksheet, 17, 'Цена за ед. без НДС', 25, start)
  buildSubHeaderCell(worksheet, 18, 'Корректировка стоимости', 25, start)
  buildSubHeaderCell(worksheet, 19, 'Сумма по позиции без НДС', 25, start)
  buildSubHeaderCell(worksheet, 20, 'Сумма, без НДС', defaultWidth, start)
  buildSubHeaderCell(worksheet, 21, 'Сумма c НДС', defaultWidth, start)
  buildSubHeaderCell(worksheet, 22, 'Всего кол-во', defaultWidth, start)

  buildMainHeaderCell(worksheet, 23, 24, 'экономия (-), перерасход (+)', start)
  buildSubHeaderCell(worksheet, 23, 'Кол-во', defaultWidth, start)
  buildSubHeaderCell(worksheet, 24, 'Сумма, без НДС', defaultWidth, start)
}

const exportToXLS = async (data, project, sheetName = 'Отчет') => {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(sheetName)

  buildReportDate(worksheet, 2)

  buildProjectName(worksheet, project, 4)

  const start = 6

  buildHeader(worksheet, start)

  data.reduce((nextIndex, item) => {
    const name = item.name
    const implementations = item.implementations
      .filter((implementation) => implementation?.name && implementation?.name?.length > 0)
      .map((implementation) => implementation.name)
    const categories = item.application_items
      .map((item) => item.category || item?.estimate_field?.category)
      .reduce((acc, item) => {
        if (acc.indexOf(item) === -1) {
          acc.push(item)
        }
        return acc
      }, [])
    const offBudgets = item.application_items.map((item) => getOffBudget(item?.off_budget))
    const estimates = item.estimate_items.map((item) => item?.name)
    const estimateCount = item.estimate_items.map((item) => item?.amount)
    const estimateUnit = item.estimate_items.map((item) => item?.unit)
    const estimatePrice = item.estimate_items.map((item) => item?.price)
    const estimateSum = item.estimate_items.reduce((acc, item) => acc + item.price * item.amount, 0)
    const applicationIds = item.application_items.map((item) => item?.application_id)
    const applicationDates = item.application_items.map((item) => format(new Date(item?.application_date), 'dd-MM-yy'))
    const applicationCount = item.application_items.map((item) => item?.amount)
    const applicationUnit = item.application_items.map((item) => item?.unit)
    const implementationCount = item.implementations.map((item) => calcSumAmount(item))
    const implementationCountCorrected = item.implementations.map((item) => item?.correctedAmount)
    const implementationUnit = item.implementations.map((item) => item?.unit)
    const implementationPrice = item.implementations.map((item, index, arr) => priceNoVat(item))

    const implementedPriceCorrected = item.implementations.map((item, index, arr) => correctedPriceNoVat(item))
    const sumsNoVat = item.implementations.map((implementation, index, arr) => priceSumNoVat(implementation))

    const sumObject = sumNoVatByUnitWithCorrect(item.implementations, item.estimate_items)
    const implementationSums = Object.values(sumObject).map((implItem) => implItem.sum)

    const vatSumObject = sumVatByUnitWithCorrect(item.implementations, item.estimate_items)

    const implementationVatSums = Object.values(vatSumObject).map((implItem) => implItem?.sum)

    const countSumObject = item.implementations.reduce((acc, implItem) => {
      const estimateItem = item.estimate_items?.find(
        (estimate) => estimate.id === implItem.application_item?.estimate_field?.id
      )
      if (implItem.correctedAmount && estimateItem) {
        if (!acc[estimateItem.unit]) {
          acc[estimateItem.unit] = {}
          acc[estimateItem.unit].sum = 0
          acc[estimateItem.unit].unit = estimateItem.unit
        }
        acc[estimateItem.unit].sum += implItem.correctedAmount
      } else if (implItem.unit && implItem.amount) {
        const finalAmount = calcSumAmount(item)
        if (!acc[implItem.unit]) {
          acc[implItem.unit] = {}
          acc[implItem.unit].sum = 0
          acc[implItem.unit].unit = implItem.unit
        }
        acc[implItem.unit].sum += finalAmount
      }
      return acc
    }, {})

    const countDifferences = Object.values(countSumObject)
      .map((implItem, index, arr) => {
        const estimateItems = item.estimate_items
        const estimateAmount = estimateItems
          .filter((estimate) => estimate.unit === implItem.unit)
          .reduce((acc, estimate) => acc + estimate.amount, 0)
        if (estimateAmount >= 0) {
          return estimateAmount - implItem?.sum
        }
        return null
      })
      .filter(Number)

    const sumCorrectedObject = sumNoVatByUnitWithCorrect(item.implementations, item.estimate_items)

    const sumDifferences = Object.values(sumCorrectedObject)
      .map((implItem, index, arr) => {
        const estimateItems = item.estimate_items
        const estimateItem = estimateItems
          .filter((estimate) => estimate.unit === implItem.unit)
          .reduce((acc, estimate) => acc + estimate?.price * estimate?.amount, 0)
        if (estimateItem >= 0) {
          return estimateItem - implItem?.sum
        }
        return null
      })
      .filter(Number)

    const implementationAmountSum = calcImplSumAmount(item.implementations)

    const highestIndex = max([implementations.length, categories.length, offBudgets.length, estimates.length])

    const newIndex = highestIndex > 1 ? nextIndex + highestIndex : nextIndex + 1

    if (highestIndex > 1) {
      worksheet.mergeCells(nextIndex, 1, newIndex - 1, 1)
      worksheet.mergeCells(nextIndex, 9, newIndex - 1, 9)
      worksheet.mergeCells(nextIndex, 22, newIndex - 1, 22)
    }

    const mergeCells = (rows, cellIndex) => {
      if (rows.length <= 1 && highestIndex > 1) {
        worksheet.mergeCells(nextIndex, cellIndex, newIndex - 1, cellIndex)
      }
    }

    mergeCells(implementations, 2)
    mergeCells(categories, 3)
    mergeCells(estimates, 5)
    mergeCells(estimateCount, 6)
    mergeCells(estimateUnit, 7)
    mergeCells(estimatePrice, 8)
    mergeCells(implementationSums, 20)
    mergeCells(implementationVatSums, 21)
    mergeCells(countDifferences, 23)
    mergeCells(sumDifferences, 24)

    const buildRows = (rows, cellIndex) =>
      rows.forEach((row, index) => buildCell(worksheet, row, cellIndex, nextIndex + index))

    buildCell(worksheet, name, 1, nextIndex)
    // implementation name
    buildRows(implementations, 2)
    buildRows(categories, 3)
    buildRows(offBudgets, 4)
    buildRows(estimates, 5)
    buildRows(estimateCount, 6)
    buildRows(estimateUnit, 7)
    buildRows(estimatePrice, 8)
    buildCell(worksheet, estimateSum, 9, nextIndex)
    buildRows(applicationIds, 10)
    buildRows(applicationDates, 11)
    buildRows(applicationCount, 12)
    buildRows(applicationUnit, 13)
    buildRows(implementationCount, 14)
    buildRows(implementationUnit, 15)
    buildRows(implementationCountCorrected, 16)
    buildRows(implementationPrice, 17)
    buildRows(implementedPriceCorrected, 18)
    buildRows(sumsNoVat, 19)
    buildRows(implementationSums, 20)
    buildRows(implementationVatSums, 21)
    buildCell(worksheet, implementationAmountSum, 22, nextIndex)
    buildRows(countDifferences, 23)
    buildRows(sumDifferences, 24)

    return newIndex
  }, start + 2)

  const buffer = await workbook.xlsx.writeBuffer()
  FileSaver.saveAs(new Blob([buffer]), 'result.xlsx')
}

export default exportToXLS
